import { doc, updateDoc, addDoc, collection, getDocs, writeBatch, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db, appId } from '../config/firebase';

/**
 * Update an opportunity
 */
export const updateOpportunity = async (userId, opportunityId, updates) => {
  if (!db || !userId) {
    throw new Error('Database or user not available.');
  }
  
  const opportunityRef = doc(
    db,
    `artifacts/${appId}/users/${userId}/opportunities`,
    opportunityId
  );
  
  await updateDoc(opportunityRef, {
    ...updates,
    lastUpdated: serverTimestamp()
  });
};

/**
 * Add property from opportunity to tracked properties
 */
export const addPropertyFromOpportunity = async (userId, opportunity) => {
  if (!db || !userId) {
    throw new Error('Database or user not available.');
  }
  
  // Check if property already exists in tracked properties
  const propertiesPath = `artifacts/${appId}/users/${userId}/trackedProperties`;
  
  const propertyData = {
    url: opportunity.propertyUrl,
    propertyId: opportunity.propertyId,
    platform: opportunity.platform,
    title: opportunity.title,
    area: opportunity.area?.toString() || '',
    bedrooms: opportunity.bedrooms?.toString() || '',
    bathrooms: opportunity.bathrooms?.toString() || '',
    imageURL: opportunity.imageUrl || '',
    currency: opportunity.currency || 'EUR',
    priceOnRequest: opportunity.priceOnRequest || false,
    sellerType: opportunity.sellerType || 'unknown',
    distress: opportunity.distress || {
      acquired: false, auction: false, reducedPrice: false,
      startPrice: null, auctionDate: null, deposit: null,
    },
    isDistressed: opportunity.isDistressed || false,
    notes: `Added from opportunity tracker.\n\nInvestment Score: ${opportunity.aiAnalysis?.investmentScore}/10\nDiscount: ${opportunity.bestDiscount}%\n\nAI Analysis: ${opportunity.aiAnalysis?.reasoning || 'N/A'}`,
    forSale: opportunity.forSale || false,
    forRent: opportunity.forRent || false,
    firstTrackedTimestamp: serverTimestamp(),
    history: [{
      price: opportunity.price,
      reportedPublishedDate: opportunity.publishedDate || 'N/A',
      reportedUpdateDate: opportunity.updatedDate || 'N/A',
      snapshotTimestamp: Timestamp.now()
    }],
    lastKnownPrice: opportunity.price,
    lastReportedUpdateDate: opportunity.updatedDate || 'N/A',
    lastModifiedTimestamp: serverTimestamp(),
    isFavorite: false,
    addedFromOpportunity: true,
    opportunityId: opportunity.id
  };
  
  await addDoc(collection(db, propertiesPath), propertyData);
};

/**
 * Dismiss an opportunity
 */
export const dismissOpportunity = async (userId, opportunityId, reason = 'user-action') => {
  await updateOpportunity(userId, opportunityId, {
    status: 'dismissed',
    dismissedAt: serverTimestamp(),
    dismissedReason: reason
  });
};

/**
 * Mark opportunity as viewed
 */
export const markOpportunityViewed = async (userId, opportunityId) => {
  await updateOpportunity(userId, opportunityId, {
    viewed: true,
    viewedAt: serverTimestamp()
  });
};

/**
 * Delete all opportunities for a user (batch delete, max 500 per batch).
 * Returns the number of deleted documents.
 */
export const clearAllOpportunities = async (userId) => {
  if (!db || !userId) {
    throw new Error('Database or user not available.');
  }

  const opportunitiesPath = `artifacts/${appId}/users/${userId}/opportunities`;
  const snap = await getDocs(collection(db, opportunitiesPath));

  if (snap.empty) return 0;

  const batchSize = 500;
  const docs = snap.docs;
  let deleted = 0;

  for (let i = 0; i < docs.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = docs.slice(i, i + batchSize);
    chunk.forEach((d) => batch.delete(d.ref));
    await batch.commit();
    deleted += chunk.length;
  }

  return deleted;
};

/**
 * Get opportunity statistics
 */
export const getOpportunityStats = (opportunities) => {
  const active = opportunities.filter(o => o.status === 'active');
  const dismissed = opportunities.filter(o => o.status === 'dismissed');
  const tracked = opportunities.filter(o => o.status === 'tracked');
  
  const avgScore = active.length > 0
    ? active.reduce((sum, o) => sum + (o.aiAnalysis?.investmentScore || 0), 0) / active.length
    : 0;
  
  const avgDiscount = active.length > 0
    ? active.reduce((sum, o) => sum + (o.bestDiscount || 0), 0) / active.length
    : 0;
  
  return {
    total: opportunities.length,
    active: active.length,
    dismissed: dismissed.length,
    tracked: tracked.length,
    avgScore: avgScore.toFixed(1),
    avgDiscount: avgDiscount.toFixed(1)
  };
};
